import React, { useState, useEffect } from 'react';
import { XMarkIcon, ChatBubbleLeftIcon, PaperAirplaneIcon, TrashIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Lead, LeadComment } from '../../types/lead';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, query, where, onSnapshot, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import toast from 'react-hot-toast';
import ConfirmDeleteModal from '../common/ConfirmDeleteModal';

interface LeadCommentsModalProps {
  lead: Lead;
  isOpen: boolean;
  onClose: () => void;
}

const LeadCommentsModal: React.FC<LeadCommentsModalProps> = ({ lead, isOpen, onClose }) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<LeadComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !lead.id) return;

    const q = query(
      collection(db, 'leadComments'),
      where('leadId', '==', lead.id)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const commentsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate() : new Date(doc.data().createdAt),
        updatedAt: doc.data().updatedAt?.toDate ? doc.data().updatedAt.toDate() : undefined,
      })) as LeadComment[];
      
      // Kliens oldali rendezés (legújabb elöl)
      commentsData.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      
      setComments(commentsData);
    });

    return () => unsubscribe();
  }, [isOpen, lead.id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !user) return;

    setIsSubmitting(true);
    try {
      const now = new Date();
      await addDoc(collection(db, 'leadComments'), {
        leadId: lead.id,
        organizationId: user.organizationId,
        content: newComment.trim(),
        createdBy: user.uid,
        createdByName: user.displayName || user.email || 'Ismeretlen',
        createdAt: now,
        isInternal: false,
      });

      setNewComment('');
      toast.success('Megjegyzés hozzáadva!');
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Hiba történt a megjegyzés hozzáadása során!');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = (commentId: string) => {
    setCommentToDelete(commentId);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await deleteDoc(doc(db, 'leadComments', commentToDelete));
      toast.success('Megjegyzés törölve!');
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error('Hiba történt a megjegyzés törlése során!');
    } finally {
      setCommentToDelete(null);
    }
  };

  const handleStartEdit = (comment: LeadComment) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editingContent.trim()) return;

    try {
      await updateDoc(doc(db, 'leadComments', commentId), {
        content: editingContent.trim(),
        updatedAt: new Date(),
      });
      setEditingCommentId(null);
      setEditingContent('');
      toast.success('Megjegyzés frissítve!');
    } catch (error) {
      console.error('Error updating comment:', error);
      toast.error('Hiba történt a megjegyzés frissítése során!');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-4xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        {/* Modern Header with Gradient */}
        <div className="bg-gradient-to-r from-primary-600 via-purple-600 to-pink-600 p-6 rounded-t-xl">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4 flex-1">
              <div className="p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                <ChatBubbleLeftIcon className="h-7 w-7 text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-white mb-2">
                  {lead.name}
                </h2>
                <div className="flex flex-wrap gap-3 text-sm text-white/90">
                  {lead.email && (
                    <a 
                      href={`mailto:${lead.email}`}
                      className="flex items-center space-x-1.5 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <span>{lead.email}</span>
                    </a>
                  )}
                  {lead.phone && (
                    <a 
                      href={`tel:${lead.phone}`}
                      className="flex items-center space-x-1.5 hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      <span>{lead.phone}</span>
                    </a>
                  )}
                  {lead.company && (
                    <div className="flex items-center space-x-1.5">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      <span>{lead.company}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-lg transition-all"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Comments List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {comments.length === 0 ? (
            <div className="text-center py-12">
              <ChatBubbleLeftIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400">
                Még nincsenek megjegyzések
              </p>
            </div>
          ) : (
            comments.map((comment) => {
              const isToday = new Date(comment.createdAt).toDateString() === new Date().toDateString();
              
              return (
              <div
                key={comment.id}
                className={`bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 space-y-2 group relative ${
                  isToday ? 'border-l-4 border-green-500' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {comment.createdByName}
                  </span>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      {format(comment.createdAt, 'yyyy. MM. dd. HH:mm', { locale: hu })}
                      {comment.updatedAt && ' (szerkesztve)'}
                    </span>
                    {comment.createdBy === user?.uid && editingCommentId !== comment.id && (
                      <>
                        <button
                          onClick={() => handleStartEdit(comment)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all"
                          title="Szerkesztés"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteComment(comment.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-all"
                          title="Törlés"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {editingCommentId === comment.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editingContent}
                      onChange={(e) => setEditingContent(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                      rows={3}
                      autoFocus
                    />
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleSaveEdit(comment.id)}
                        className="px-3 py-1 bg-primary-600 hover:bg-primary-700 text-white text-sm rounded-lg transition-colors flex items-center space-x-1"
                      >
                        <CheckIcon className="h-4 w-4" />
                        <span>Mentés</span>
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-gray-300 hover:bg-gray-400 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-700 dark:text-white text-sm rounded-lg transition-colors"
                      >
                        Mégse
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                    {comment.content}
                  </p>
                )}
              </div>
              );
            })
          )}
        </div>

        {/* New Comment Form */}
        <form onSubmit={handleSubmit} className="p-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex space-x-3">
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Írj megjegyzést..."
              rows={3}
              className="flex-1 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
              disabled={isSubmitting}
            />
            <button
              type="submit"
              disabled={!newComment.trim() || isSubmitting}
              className="px-6 py-3 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 dark:disabled:bg-gray-600 text-white rounded-lg transition-colors flex items-center space-x-2 self-end"
            >
              <PaperAirplaneIcon className="h-5 w-5" />
              <span>Küldés</span>
            </button>
          </div>
        </form>
      </div>

      <ConfirmDeleteModal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          setIsDeleteConfirmOpen(false);
          setCommentToDelete(null);
        }}
        onConfirm={confirmDeleteComment}
        title="Megjegyzés törlése"
        deleteMessage="Biztosan törölni szeretnéd ezt a megjegyzést?"
        deleteWarning="Ez a művelet nem vonható vissza."
      />
    </div>
  );
};

export default LeadCommentsModal;
